import Cropper, { Area } from 'react-easy-crop';
import { useState, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import { upload_blog_cover } from '@/api/blog';

interface CoverImageUploaderProps {
  blogId: string;
  initialImage?: string | null;
}

const FIXED_WIDTH = 1200;
const FIXED_HEIGHT = 630;
const ASPECT = FIXED_WIDTH / FIXED_HEIGHT;

const CoverImageUploader = forwardRef((props: CoverImageUploaderProps, ref) => {
  const { blogId, initialImage } = props;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (initialImage) {
      setPreviewUrl(initialImage);
    }
  }, [initialImage]);

  /* =========================
     Handle file selection
  ========================= */
  const handleFile = (file: File) => {
    console.log('Selected file:', file);
    setImageSrc(URL.createObjectURL(file));
    setPreviewUrl(null);
  };

  const onCropComplete = useCallback((_: Area, croppedArea: Area) => {
    setCroppedAreaPixels(croppedArea);
  }, []);

  /* =========================
     Generate cropped blob
  ========================= */
  const generateCroppedBlob = useCallback(async (): Promise<Blob | null> => {
    if (!imageSrc || !croppedAreaPixels) return null;

    const image = new Image();
    image.src = imageSrc;
    await new Promise((res) => (image.onload = res));

    const canvas = document.createElement('canvas');
    canvas.width = FIXED_WIDTH;
    canvas.height = FIXED_HEIGHT;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      FIXED_WIDTH,
      FIXED_HEIGHT
    );

    return new Promise((resolve) =>
      canvas.toBlob((blob) => {
        if (blob) {
          console.log('Generated blob:', blob);
          setPreviewUrl(URL.createObjectURL(blob));
          resolve(blob);
        } else {
          console.warn('Failed to generate blob');
          resolve(null);
        }
      }, 'image/jpeg')
    );
  }, [imageSrc, croppedAreaPixels]);

  /* =========================
     Expose upload method to parent
  ========================= */
  useImperativeHandle(ref, () => ({
    uploading,
    uploadImage: async (id?: string): Promise<string | null> => {
      const targetBlogId = id || blogId;
      console.log('Upload started for blogId:', targetBlogId);


      if (!imageSrc) {
        if (previewUrl && previewUrl.startsWith('http')) {
          return previewUrl;
        }
        console.warn('No image selected');
        return null;
      }

      if (!targetBlogId) {
        console.warn('Blog ID missing');
        return null;
      }

      setUploading(true);

      try {
        const blob = await generateCroppedBlob();
        if (!blob) {
          console.warn('Blob generation failed');
          setUploading(false);
          return null;
        }

        const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
        console.log('Uploading file:', file);

        const res = await upload_blog_cover(targetBlogId, file);
        console.log('Upload response:', res);

        setUploading(false);
        return res.cover_image ?? null;
      } catch (err) {
        console.error('Upload failed:', err);
        setUploading(false);
        return null;
      }
    },
  }));

  return (
    <div className="border rounded-xl p-2 mx-auto w-full max-w-[1200px] space-y-4">
      <div
        className="relative w-full bg-gray-100 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center overflow-hidden"
        style={{ paddingTop: `${(FIXED_HEIGHT / FIXED_WIDTH) * 100}%` }}
      >
        {!imageSrc && (
          <>
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              Click or drag image here
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files && handleFile(e.target.files[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </>
        )}

        {imageSrc && (
          <Cropper
            image={imageSrc}
            aspect={ASPECT}
            crop={crop}
            zoom={zoom}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        )}
      </div>

      {imageSrc && (
        <div className="flex items-center justify-between gap-2 mt-2">
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
          <button
            className="px-3 py-1 border rounded"
            onClick={() => {
              setImageSrc(null);
              setPreviewUrl(null);
            }}
          >
            Delete / Change Image
          </button>
        </div>
      )}

      {previewUrl && (
        <img
          src={previewUrl}
          alt="cover preview"
          className="w-full rounded-md object-cover mt-2"
        />
      )}
    </div>
  );
});

export default CoverImageUploader;
